import type {
  PublicPetitionSaveStep1,
  PublicPetitionSaveStep2,
  PublicPetitionSaveStep3,
  PublicPetitionSaveStep4,
  PublicPetitionSaveStep5,
  PublicPetitionSaveStep5File,
  PublicPetitionStepsResponse,
  SavePublicPetitionStepsPayload,
} from '../types/publicPetitionApi';
import type { PublicPetitionWizardDraft } from '../types/publicPetitionCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull, apiStepString } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { PublicPetitionWizardStep } from '../pages/wizard/publicPetition/publicPetitionConstants';
import {
  FILE_CATEGORY_PUBLIC_PETITION,
  PUBLIC_PETITION_CATEGORY_ID,
} from '../pages/wizard/publicPetition/publicPetitionConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';

export type { PublicPetitionWizardStep };

export function caseRequestIdFromResponse(response: PublicPetitionStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: PublicPetitionStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function stepProcessed(
  response: PublicPetitionStepsResponse,
  camel: keyof PublicPetitionStepsResponse,
  snake: keyof PublicPetitionStepsResponse,
): boolean | undefined {
  const c = response[camel];
  if (typeof c === 'boolean') return c;
  const s = response[snake];
  if (typeof s === 'boolean') return s;
  return undefined;
}

function step1LooksComplete(s1: PublicPetitionSaveStep1 | undefined, _response: PublicPetitionStepsResponse): boolean {
  const desc = (s1?.description ?? '').trim();
  const matter =
    s1?.case_matter === 'CRIMINAL' || s1?.case_matter === 'CIVIL'
      ? s1.case_matter
      : typeof s1?.is_criminal_matter === 'boolean'
        ? s1.is_criminal_matter
          ? 'CRIMINAL'
          : 'CIVIL'
        : '';
  const province = (s1?.province_id ?? '').trim();
  const ref = (s1?.external_case_reference_number ?? '').trim();
  // Public Petitions have no sub file category (see subFileCategoryRules).
  return Boolean(desc && matter && province && ref);
}

function step2LooksComplete(s2: PublicPetitionSaveStep2 | undefined): boolean {
  return Boolean((s2?.petitioner_name ?? '').trim() && (s2?.respondent_names ?? '').trim());
}

function step3LooksComplete(s3: PublicPetitionSaveStep3 | undefined): boolean {
  return Boolean((s3?.urgency_level_id ?? '').trim());
}

function step4LooksComplete(s4: PublicPetitionSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5LooksComplete(s5: PublicPetitionSaveStep5 | undefined): boolean {
  return step5HasAnyData(s5);
}

function step2HasAnyData(s2: PublicPetitionSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.petitioner_name ?? '').trim() ||
      (s2?.respondent_names ?? '').trim() ||
      (s2?.respondent_type_id ?? '').trim(),
  );
}

function step3HasAnyData(s3: PublicPetitionSaveStep3 | undefined): boolean {
  return Boolean(
    (s3?.urgency_level_id ?? '').trim() ||
      (s3?.deadline ?? '').trim() ||
      (s3?.urgency_justification ?? '').trim(),
  );
}

function step4HasAnyData(s4: PublicPetitionSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5HasAnyData(s5: PublicPetitionSaveStep5 | undefined): boolean {
  const files = step5DocumentsFromApi(s5);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

function latestSavedPublicPetitionStep(response: PublicPetitionStepsResponse): PublicPetitionWizardStep {
  if (step5HasAnyData(response.step_5)) return 5;
  if (step4HasAnyData(response.step_4)) return 4;
  if (step3HasAnyData(response.step_3)) return 3;
  if (step2HasAnyData(response.step_2)) return 2;
  if (step1LooksComplete(response.step_1, response)) return 1;
  if (
    (response.step_1?.description ?? '').trim() ||
    (response.step_1?.province_id ?? '').trim() ||
    (response.step_1?.external_case_reference_number ?? '').trim()
  ) {
    return 1;
  }
  return 1;
}

export function resolvePublicPetitionDraftResumeStep(response: PublicPetitionStepsResponse): PublicPetitionWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (stepProcessed(response, 'step1Processed', 'step_1_processed') === false) {
    const latest = latestSavedPublicPetitionStep(response);
    return latest > 1 ? latest : 1;
  }
  if (stepProcessed(response, 'step2Processed', 'step_2_processed') === false) return 2;
  if (stepProcessed(response, 'step3Processed', 'step_3_processed') === false) return 3;
  if (stepProcessed(response, 'step4Processed', 'step_4_processed') === false) return 4;
  if (stepProcessed(response, 'step5Processed', 'step_5_processed') === false) return 5;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  if (!step1LooksComplete(s1, response)) {
    const latest = latestSavedPublicPetitionStep(response);
    return latest > 1 ? latest : 1;
  }
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  if (!step5LooksComplete(s5)) return 5;
  return 5;
    },
    (step) => step >= 1 && step <= 5,
  );
}

function step5DocumentsFromApi(s5: PublicPetitionSaveStep5 | undefined): PublicPetitionSaveStep5File[] {
  return [
    ...(s5?.uploaded_files ?? []),
    ...(s5?.extra_uploaded_files ?? []),
    ...(s5?.documents ?? []),
  ];
}

function apiDocumentTypeId(file: PublicPetitionSaveStep5File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function step5FileFields(file: PublicPetitionSaveStep5File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step5FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep4(s4: PublicPetitionSaveStep4 | undefined): string {
  const raw = s4?.other_method;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s4: PublicPetitionSaveStep4 | undefined): string {
  const raw = s4?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(response: PublicPetitionStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: PublicPetitionStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function resolveCaseMatter(
  s1: PublicPetitionSaveStep1 | undefined,
  prev: PublicPetitionWizardDraft['caseMatter'],
): PublicPetitionWizardDraft['caseMatter'] {
  if (s1?.case_matter === 'CRIMINAL' || s1?.case_matter === 'CIVIL') return s1.case_matter;
  if (typeof s1?.is_criminal_matter === 'boolean') return s1.is_criminal_matter ? 'CRIMINAL' : 'CIVIL';
  if (prev === 'CRIMINAL' || prev === 'CIVIL') return prev;
  return 'CRIMINAL';
}

function parseNumberOfRespondents(value: string | undefined): number | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapStep1(draft: PublicPetitionWizardDraft): SaveStepFields<PublicPetitionSaveStep1> {
  const caseMatter =
    draft.caseMatter === 'CRIMINAL' || draft.caseMatter === 'CIVIL' ? draft.caseMatter : 'CRIMINAL';
  return {
    description: strOrNull(draft.description),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    is_criminal_matter: caseMatter === 'CRIMINAL',
    province_id: strOrNull(draft.provinceId),
    province_name: strOrNull(draft.provinceName),
    district_id: strOrNull(draft.districtId),
    district_name: strOrNull(draft.districtName),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_available:
      draft.hasConnectedMatters === 'yes' ? draft.connectedAgdFileRefAvailable : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' && draft.connectedAgdFileRefAvailable
        ? strOrNull(draft.connectedAgdFileRef)
        : undefined,
  };
}

function mapStep2(draft: PublicPetitionWizardDraft): SaveStepFields<PublicPetitionSaveStep2> {
  const respondentTypeName = strOrNull(draft.respondentTypeName);
  const numberOfRespondents = parseNumberOfRespondents(draft.numberOfRespondents);
  return {
    petitioner_name: strOrNull(draft.petitionerName),
    nic_or_identification_number: strOrNull(draft.nicOrIdentificationNumber),
    address: strOrNull(draft.petitionerAddress),
    contact_number: strOrNull(draft.petitionerContactNumber),
    email_address: strOrNull(draft.petitionerEmail),
    is_represented_by_attorney: draft.representedByAttorney,
    attorney_name: draft.representedByAttorney ? strOrNull(draft.attorneyName) : null,
    attorney_contact_number: draft.representedByAttorney ? strOrNull(draft.attorneyContactNumber) : null,
    respondent_names: strOrNull(draft.respondentNames),
    respondent_type_id: strOrNull(draft.respondentTypeId),
    respondent_type_name: respondentTypeName,
    respondent_address: strOrNull(draft.respondentAddress),
    number_of_respondents: numberOfRespondents,
  };
}

function mapStep3(draft: PublicPetitionWizardDraft): SaveStepFields<PublicPetitionSaveStep3> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep4(draft: PublicPetitionWizardDraft): SaveStepFields<PublicPetitionSaveStep4> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  return {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
    other_method: draftHasOtherDeliverySelected(draft) ? strOrNull(draft.deliveryOther) : null,
  };
}

function mapStep5(draft: PublicPetitionWizardDraft): SaveStepFields<PublicPetitionSaveStep5> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

function categoryIdForSave(draft: PublicPetitionWizardDraft): string | undefined {
  return strOrNull(draft.fileCategoryId) || strOrNull(PUBLIC_PETITION_CATEGORY_ID) || undefined;
}

export function buildSavePublicPetitionPayload(
  draft: PublicPetitionWizardDraft,
  options: {
    currentStep?: PublicPetitionWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SavePublicPetitionStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SavePublicPetitionStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  const categoryId = categoryIdForSave(draft);
  if (categoryId) payload.category_id = categoryId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    if (step >= 5) payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);
  if (step === 5) payload.step_5 = mapStep5(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapPublicPetitionStepsToDraft(
  response: PublicPetitionStepsResponse,
  prev: PublicPetitionWizardDraft,
): PublicPetitionWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;
  const hasStep1 = s1 != null;
  const hasStep2 = s2 != null;
  const hasStep3 = s3 != null;
  const hasStep4 = s4 != null;

  const documents = (() => {
    const fromApi = step5DocumentsFromApi(s5);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId
            ? isDocumentTypeUuid(typeId)
            : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = step5FileFields(f);
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = step5FileFields(match);
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(s4?.physical_file_delivery_methods);
  const deliveryIds = s4?.physical_file_delivery_method_ids?.length
    ? s4.physical_file_delivery_method_ids
    : delivery.ids;

  const fileCategoryId =
    (response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId) || PUBLIC_PETITION_CATEGORY_ID;
  const fileCategoryName =
    (response.file_category_name ?? s1?.category_name ?? prev.fileCategory) || FILE_CATEGORY_PUBLIC_PETITION;
  const subFileCategoryId =
    response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName;

  const connectedAgdAvailable = (() => {
    if (s1?.connected_agd_file_ref_available != null) return Boolean(s1.connected_agd_file_ref_available);
    return prev.connectedAgdFileRefAvailable;
  })();

  const caseMatter = resolveCaseMatter(s1, prev.caseMatter);

  const numberOfRespondentsRaw = s2?.number_of_respondents;
  const numberOfRespondents = hasStep2
    ? numberOfRespondentsRaw == null || numberOfRespondentsRaw === ''
      ? ''
      : String(numberOfRespondentsRaw)
    : prev.numberOfRespondents;

  return {
    ...prev,
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: apiStepString(hasStep1, s1?.description, prev.description),
    caseMatter,
    provinceId: apiStepString(hasStep1, s1?.province_id, prev.provinceId),
    provinceName: apiStepString(hasStep1, s1?.province_name, prev.provinceName),
    districtId: apiStepString(hasStep1, s1?.district_id, prev.districtId),
    districtName: apiStepString(hasStep1, s1?.district_name, prev.districtName),
    yourReferenceNumber: apiStepString(hasStep1, s1?.external_case_reference_number, prev.yourReferenceNumber),
    otherExternalReferences: apiStepString(
      hasStep1,
      s1?.other_external_case_reference_number,
      prev.otherExternalReferences,
    ),
    caseNumber: apiStepString(hasStep1, s1?.case_number, prev.caseNumber),
    hasConnectedMatters: hasStep1
      ? boolToYesNo(s1?.has_connected_case) || 'no'
      : prev.hasConnectedMatters || 'no',
    connectedCaseNumber: apiStepString(hasStep1, s1?.connected_case_number, prev.connectedCaseNumber),
    connectedAgdFileRefAvailable: connectedAgdAvailable,
    connectedAgdFileRef: apiStepString(hasStep1, s1?.connected_agd_file_ref_number, prev.connectedAgdFileRef),
    petitionerName: apiStepString(hasStep2, s2?.petitioner_name, prev.petitionerName),
    nicOrIdentificationNumber: apiStepString(
      hasStep2,
      s2?.nic_or_identification_number,
      prev.nicOrIdentificationNumber,
    ),
    petitionerAddress: apiStepString(
      hasStep2,
      s2?.address ?? s2?.petitioner_address,
      prev.petitionerAddress,
    ),
    petitionerContactNumber: apiStepString(
      hasStep2,
      s2?.contact_number ?? s2?.petitioner_contact_number,
      prev.petitionerContactNumber,
    ),
    petitionerEmail: apiStepString(hasStep2, s2?.email_address ?? s2?.petitioner_email, prev.petitionerEmail),
    representedByAttorney: hasStep2
      ? (s2?.is_represented_by_attorney ?? s2?.represented_by_attorney ?? prev.representedByAttorney)
      : prev.representedByAttorney,
    attorneyName: apiStepString(hasStep2, s2?.attorney_name, prev.attorneyName),
    attorneyContactNumber: apiStepString(hasStep2, s2?.attorney_contact_number, prev.attorneyContactNumber),
    respondentNames: apiStepString(hasStep2, s2?.respondent_names, prev.respondentNames),
    respondentTypeId: apiStepString(hasStep2, s2?.respondent_type_id, prev.respondentTypeId),
    respondentTypeName: apiStepString(hasStep2, s2?.respondent_type_name, prev.respondentTypeName),
    respondentAddress: apiStepString(hasStep2, s2?.respondent_address, prev.respondentAddress),
    numberOfRespondents,
    urgencyLevelId: apiStepString(hasStep3, s3?.urgency_level_id, prev.urgencyLevelId),
    urgencyLevelName: apiStepString(hasStep3, s3?.urgency_level_name, prev.urgencyLevelName),
    deadline: apiStepString(hasStep3, s3?.deadline, prev.deadline),
    urgencyJustification: apiStepString(hasStep3, s3?.urgency_justification, prev.urgencyJustification),
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther: hasStep4
      ? otherMethodFromStep4(s4) || otherMethodFromDeliveryRows(s4)
      : prev.deliveryOther,
    documents,
  };
}
